import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import {
  approveTriageQueueMessage,
  fetchTriageNotificationcount,
  fetchTriageProfilePic,
  fetchTriageQueueList,
  fetchTriageUserList,
} from "../../services/triageQueueServices";
import {
  ITriageQueueListState,
  ReceiverUser,
} from "../../models/triageQueueInterface";
import { IMessageTo } from "../../models/dashboardInterfaces";
import { MSG_CLIENT_ADMIN, MSG_CLIENT_ASSOCIATE, MSG_NURSING_STAFF, MSG_PROVIDER } from "../../config/permissions";
import { setNotification } from "../../components/notification/ToastNotification";
import { message } from "../../utilities/locals/messages";

export interface TriageQueueState {
  loading: "idle" | "loading" | "success" | "fetchSuccessfully" | "error";
  triageQueueListData: ITriageQueueListState[];
  paginationTotalCount: number;
  allUserList: ReceiverUser[];
  avatarUrls: Record<string, string>;
  triageNotificationCount: number;
}

const initialState: TriageQueueState = {
  loading: "idle",
  allUserList: [],
  paginationTotalCount: 0,
  triageQueueListData: [],
  avatarUrls: {},
  triageNotificationCount: 0
};

export const triageQueueDataSlice = createSlice({
  name: "triageQueueDataSlice",
  initialState,
  reducers: {
    changeLoading: (state, action: PayloadAction<any>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTriageQueueList.pending, (state) => {
      state.paginationTotalCount = 0;
      state.loading = "loading";
    });
    builder.addCase(fetchTriageQueueList.fulfilled, (state, action) => {
      state.paginationTotalCount = action.payload.count;
      const getLabelByValue = (value: string): string => {
        switch (value) {
          case MSG_CLIENT_ADMIN:
            return "Client Admin";
          case MSG_CLIENT_ASSOCIATE:
            return "Associates";
          case MSG_NURSING_STAFF:
            return "Facility Staff";
          case MSG_PROVIDER:
            return "Providers";
          default:
            return value;
        }
      };

      state.triageQueueListData = action.payload.results.map(
        (item: ITriageQueueListState) => ({
          ...item,          
          patient_receiver: Array.isArray(item.patient_receiver)
          ? item.patient_receiver.map((receiver) => getLabelByValue(receiver))
          : [],
          receiver:
            item.receiver?.map((r) => ({
              ...r,
              name: getLabelByValue(r.name),
              isSelected: true,
              isGroup: false,
            })) ?? [],
        })
      );

      

      state.loading = "fetchSuccessfully";
    });
    builder.addCase(fetchTriageQueueList.rejected, (state) => {
      state.loading = "error";
    });

    builder.addCase(fetchTriageProfilePic.pending, (state) => {
      state.loading = "idle";
    });
    builder.addCase(fetchTriageProfilePic.fulfilled, (state, action) => {
      state.avatarUrls = action.payload;
    });
    builder.addCase(fetchTriageProfilePic.rejected, (state) => {
      state.loading = "error";
    });

    builder.addCase(fetchTriageUserList.pending, (state) => {
      state.loading = "idle";
      state.allUserList = [];
    });

    builder.addCase(fetchTriageUserList.fulfilled, (state, action: PayloadAction<{ id: number; users: IMessageTo[] }>) => {
      state.loading = "fetchSuccessfully";


      // Step 1: Extract all receivers from triageQueueListData
      const selectedItem = state.triageQueueListData.find((item) => item.id === action.payload.id);
      const triageReceivers = selectedItem?.receiver || [];

      

      // Step 2: Create a Set of receiver values (for comparison)
      const receiverUserIds = new Set(triageReceivers.filter((r): r is ReceiverUser => !!r?.user_id).map((r) => r.user_id));
      const groupValues = new Set(
        triageReceivers.filter(r => !r.user_id).map(r => r.value)
      );
      
      const incomingUsers = action.payload.users
        .filter(
          (item: IMessageTo) => !receiverUserIds.has(Number(item.user_id))
        )
        .map((item: IMessageTo) => ({
          user_id: Number(item.user_id),
          name: item.user_name,
          value: item.email,
          group_name: item.group_name,
          isGroup:  groupValues.has(item.group_name),
          isSelected: groupValues.has(item.group_name), // Default for new users
        }));

        
      state.allUserList = [...incomingUsers, ...triageReceivers] as ReceiverUser[];
    });

    builder.addCase(fetchTriageUserList.rejected, (state) => {
      state.loading = "error";
    });

    builder.addCase(approveTriageQueueMessage.pending, (state) => {
      state.loading = "loading";
    });
    builder.addCase(approveTriageQueueMessage.fulfilled, (state) => {
      
     state.triageNotificationCount = Math.max(0, state.triageNotificationCount - 1);

      state.loading = "success";
      setNotification(
        message.CFPCareConnect,
        message.messageSentSuccessfully,
        false,
        'success',
        'approveTriageQueueMessage'
      )
    });
    builder.addCase(approveTriageQueueMessage.rejected, (state) => {
      state.loading = "error";
      setNotification(
        message.CFPCareConnect,
        message.messageSentFailed,
        false,
        'failed',
        'approveTriageQueueMessage'
      )
    });

    builder.addCase(fetchTriageNotificationcount.pending, (state) => {
      state.loading = "idle";
    });
    builder.addCase(fetchTriageNotificationcount.fulfilled, (state,action) => {
      if (state.triageNotificationCount !== action.payload.triage_count) {
        state.triageNotificationCount = action.payload.triage_count        
        state.loading = "success";
      }
    });
    builder.addCase(fetchTriageNotificationcount.rejected, (state) => {
      state.loading = "error";
    });
  },
});

// Action creators are generated for each case reducer function
export const { changeLoading } = triageQueueDataSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const TriageQueueDataSliceSlices = (state: RootState) =>
  state.triageQueueData;

export const triageQueueReducer = triageQueueDataSlice.reducer;
